import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { StatusBadge, LoadingSpinner } from '../common/Badges';
import InvoiceView from './InvoiceView';

const CustomerInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);

  useEffect(() => { loadInvoices(); }, []);

  const loadInvoices = async () => {
    try { const data = await api('/api/invoices'); setInvoices(data); }
    catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  };

  const handlePay = async (invoiceId) => {
    setPaying(invoiceId);
    try {
      const result = await api('/api/payments/checkout', { method: 'POST', body: JSON.stringify({ invoice_id: invoiceId, origin_url: window.location.origin }) });
      window.location.href = result.url;
    } catch (err) { toast.error(err.message); setPaying(null); }
  };

  const viewInvoiceDetail = async (inv) => {
    setViewInvoice(inv);
    try { const detail = await api(`/api/invoices/${inv.id}/detail`); setInvoiceDetail(detail); }
    catch { toast.error('Failed to load invoice detail'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="customer-invoices" className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-white">My Invoices</h1>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Invoice #</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Job</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Amount</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="hover:bg-gray-800" data-testid={`invoice-row-${invoice.id}`}>
                <td className="px-4 py-3 font-mono text-sm">{invoice.invoice_number}</td>
                <td className="px-4 py-3 font-mono text-sm">{invoice.ticket_number}</td>
                <td className="px-4 py-3 font-semibold">${invoice.total.toFixed(2)}</td>
                <td className="px-4 py-3"><StatusBadge status={invoice.status} /></td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => viewInvoiceDetail(invoice)} className="text-sm text-red-500 hover:text-red-400" data-testid={`view-invoice-${invoice.id}`}>View</button>
                  {['sent', 'signed', 'overdue'].includes(invoice.status) && (
                    <button onClick={() => handlePay(invoice.id)} disabled={paying === invoice.id} className="btn-primary text-sm py-1" data-testid={`pay-invoice-btn-${invoice.id}`}>
                      {paying === invoice.id ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'Pay'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {invoices.length === 0 && <div className="p-8 text-center text-gray-400">No invoices found.</div>}
      </div>

      {viewInvoice && <InvoiceView detail={invoiceDetail} onClose={() => { setViewInvoice(null); setInvoiceDetail(null); }} />}
    </div>
  );
};

export default CustomerInvoices;
